import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  Heading,
  Input,
  Link,
  ListItem,
  OrderedList,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { AuthLayout } from "@src/components/modules/auth/layout";
import { LoadingOverlay } from "@src/components/ui-kit";
import { useStore } from "@src/utils/hooks";
import { getQueryParamOptional } from "@src/utils/routing";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { type FunctionComponent, useEffect } from "react";
import { AuthErrorMessage } from "./error-message";

export const TwoFAConfirm: FunctionComponent = observer(
  function TwoFAConfirm() {
    const router = useRouter();
    const {
      authStore: { twoFactorAuthenticationStore },
    } = useStore();

    useEffect(() => {
      if (router.isReady) {
        twoFactorAuthenticationStore.initConfirmStep(
          getQueryParamOptional("redirect_to", router.query),
        );
      }
    }, [router.isReady]);

    if (twoFactorAuthenticationStore.confirmStatus === "error") {
      return <AuthErrorMessage />;
    }

    return (
      <AuthLayout>
        <LoadingOverlay
          isLoading={twoFactorAuthenticationStore.confirmStatus === "loading"}
          position="absolute"
        >
          <Grid gap="8" templateColumns={["", "max-content 1fr"]}>
            <GridItem as={VStack} spacing="2">
              <img
                alt={t`Scan this QR code with your two-factor authenticator app.`}
                src={twoFactorAuthenticationStore.confirmData?.qr_code_data_url}
              />
              <Text>
                {twoFactorAuthenticationStore.confirmData?.two_factor_secret}
              </Text>
            </GridItem>
            <GridItem as={Flex} direction="column" gap="4">
              <Heading as="h1" size="md">
                <Trans>Two-Factor Authentication Set Up</Trans>
              </Heading>
              <OrderedList spacing="2">
                <ListItem>
                  <Trans>
                    Download your preferred two-factor authenticator
                    application. If you don't already have one, we recommend{" "}
                    <Link
                      color="#3490dc"
                      href="https://support.google.com/accounts/answer/1066447"
                      isExternal
                    >
                      Google Authenticator
                    </Link>
                    .
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    Use your application to scan the QR code. If you are unable
                    to scan the QR code, please use the 2FA secret instead.
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    Enter the 6-digit code provided by your app and click
                    'Finish 2FA Setup' to verify.
                  </Trans>
                </ListItem>
              </OrderedList>
              <Flex
                as="form"
                gap="1"
                onSubmit={(event) => {
                  event.preventDefault();
                  twoFactorAuthenticationStore.confirmTwoFactorAuthentication();
                }}
              >
                <FormControl
                  isInvalid={
                    twoFactorAuthenticationStore.confirmForm.code.hasError
                  }
                >
                  <Input
                    flex="1"
                    autoComplete="one-time-code"
                    autoFocus
                    inputMode="numeric"
                    onChange={(event) =>
                      twoFactorAuthenticationStore.confirmForm.code.onChange(
                        event.target.value,
                      )
                    }
                    placeholder={t`Code`}
                    required
                    type="text"
                    value={twoFactorAuthenticationStore.confirmForm.code.value}
                  />
                  <FormErrorMessage>
                    {twoFactorAuthenticationStore.confirmForm.code.error}
                  </FormErrorMessage>
                </FormControl>
                <Button
                  isLoading={
                    twoFactorAuthenticationStore.confirmStatus === "submitting"
                  }
                  type="submit"
                >
                  <Trans>Confirm</Trans>
                </Button>
              </Flex>
            </GridItem>
          </Grid>
        </LoadingOverlay>
      </AuthLayout>
    );
  },
);
